<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { Head } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import KeepersLogo from '@/components/icons/KeepersLogo.vue';
    import OnboardingGuideItem from '@/components/onboarding/OnboardingGuideItem.vue';
    import type { SharedData } from '@/types';
    import OnboardingAssistant from '@/components/onboarding/OnboardingAssistant.vue';
    import { router } from '@inertiajs/vue3';
    import { Button } from '@/components/ui/button';

    const { t } = useI18n();

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const open = ref(false);
    const completedSteps = ref(false);
    const completed = ref(false);

    const steps = computed(() => {
        const allSteps = page.props.auth.onboarding_steps;
        return [...allSteps].sort((a, b) => {
            if (a.is_completed && !b.is_completed) return 1;
            if (!a.is_completed && b.is_completed) return -1;
            return 0;
        });
    });

    const firstVisit = computed(() => {
        return steps.value.every((step) => step.status === 'pending');
    });

    const translationKey = computed(() => {
        return firstVisit.value ? 'steps_intro' : 'steps_intro_missing';
    });

    const handleCompleted = (incompleteSteps: number) => {
        completedSteps.value = true;
        if (incompleteSteps === 0) {
            completed.value = true;
        }
        open.value = false;
    };

    const goToDashboard = () => {
        router.visit(route('v2.index'));
    };
</script>

<template>
    <Head :title="$t('onboarding.page_header')" />

    <div
        class="relative flex h-dvh w-full items-center justify-center overflow-y-auto bg-[url('/img/login-bg.png')] bg-cover bg-right py-8 sm:px-8 lg:max-w-none"
    >
        <KeepersLogo
            type="light"
            class="absolute top-8 left-8 hidden max-w-24 sm:max-w-36 lg:block"
        />
        <div
            class="w-full px-6 sm:px-0 lg:max-w-[500px]"
            v-if="!open && !completedSteps"
        >
            <div class="flex flex-col gap-12 text-white">
                <KeepersLogo
                    type="light"
                    class="block max-w-36 lg:hidden"
                />
                <h1 class="text-2xl font-semibold sm:text-3xl">
                    {{
                        t(`onboarding.${translationKey}.title`, { name: page.props.auth.user.name })
                    }}
                </h1>
                <div class="flex flex-col gap-4">
                    <h2 class="text-xl font-semibold sm:text-2xl">
                        {{ t(`onboarding.${translationKey}.subtitle`) }}
                    </h2>
                    <p class="text-sm font-light sm:text-lg">
                        {{ t(`onboarding.${translationKey}.content`) }}
                    </p>
                </div>

                <div class="grid auto-cols-fr grid-flow-col">
                    <OnboardingGuideItem
                        v-for="step in steps"
                        :key="step.id"
                        :onboarding-step="step"
                        type="light"
                        :class="{
                            'opacity-40': step.is_completed,
                        }"
                    />
                </div>
                <div
                    v-if="firstVisit"
                    class="flex justify-center"
                >
                    <Button
                        variant="default"
                        @click="open = true"
                    >
                        {{ t('onboarding.steps_intro.button') }}
                    </Button>
                </div>
                <div
                    v-else
                    class="flex justify-center gap-4"
                >
                    <Button
                        variant="outline"
                        @click="goToDashboard"
                        class="text-foreground bg-white"
                    >
                        {{ t('onboarding.steps_intro_missing.skip') }}
                    </Button>
                    <Button
                        variant="default"
                        @click="open = true"
                    >
                        {{ t('onboarding.steps_intro_missing.button') }}
                    </Button>
                </div>
            </div>
        </div>
        <div
            class="w-full px-2 sm:px-0 lg:max-w-[500px]"
            v-else-if="!open && completedSteps"
        >
            <div class="flex flex-col gap-12 text-white">
                <KeepersLogo
                    type="light"
                    class="block max-w-36 lg:hidden"
                />
                <h1
                    class="text-3xl font-semibold"
                    :class="{
                        'text-center': completed,
                    }"
                >
                    {{ t('onboarding.steps_completed.title') }}
                </h1>
                <div
                    v-if="completed"
                    class="flex flex-col gap-4"
                >
                    <h2 class="text-center text-2xl font-semibold">
                        {{ t(`onboarding.steps_completed.subtitle`) }}
                    </h2>
                </div>
                <div
                    v-else
                    class="flex flex-col gap-4"
                >
                    <h2 class="text-2xl font-semibold">
                        {{ t(`onboarding.steps_completed_missing.subtitle`) }}
                    </h2>
                    <p class="text-lg font-light">
                        {{ t(`onboarding.steps_completed_missing.content`) }}
                    </p>
                </div>

                <div
                    v-if="!completed"
                    class="grid auto-cols-fr grid-flow-col"
                >
                    <OnboardingGuideItem
                        v-for="step in steps"
                        :key="step.id"
                        :onboarding-step="step"
                        type="light"
                        :class="{
                            'opacity-40': step.is_completed,
                        }"
                    />
                </div>
                <div
                    v-if="completed"
                    class="flex justify-center"
                >
                    <Button
                        variant="default"
                        @click="goToDashboard"
                    >
                        {{ t('onboarding.steps_completed.button') }}
                    </Button>
                </div>
                <div
                    v-else
                    class="flex justify-center gap-4"
                >
                    <Button
                        variant="outline"
                        @click="open = true"
                        class="text-foreground bg-white"
                    >
                        {{ t('onboarding.steps_completed_missing.back') }}
                    </Button>
                    <Button
                        variant="default"
                        @click="goToDashboard"
                    >
                        {{ t('onboarding.steps_completed_missing.button') }}
                    </Button>
                </div>
            </div>
        </div>
        <div
            class="relative my-auto flex w-full max-w-[calc(100%-2rem)] flex-col rounded-lg bg-white p-5 shadow-lg duration-200 sm:min-h-[700px] sm:max-w-2xl"
            v-if="open"
        >
            <OnboardingAssistant
                :onboarding-steps="steps"
                :company-id="selectedCompanyId"
                v-model="open"
                @completed="handleCompleted"
                @close="open = false"
            />
        </div>
    </div>
</template>
